import React, { useState, useEffect, useCallback } from 'react';
import { ServiceRequestFormData, OrganizationData } from '../../types';
import './StepStyles.scss';

interface Step5FreeRequestProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  registerValidation: (validator: () => boolean) => void;
  organizationData: OrganizationData;
}

function Step5FreeRequest({
  formData,
  updateFormData,
  registerValidation,
}: Step5FreeRequestProps) {
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormData({ requestDescription: e.target.value });
    setValidationError(null);
  };

  const validateStep = useCallback(() => {
    const description = formData.requestDescription?.trim() || '';

    if (!description) {
      setValidationError("Please describe your service request.");
      return false;
    }

    if (description.length < 10) {
      setValidationError("Please provide at least 10 characters describing your service request.");
      return false;
    }

    setValidationError(null);
    return true;
  }, [formData.requestDescription]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  return (
    <div className="form-step step-free-request">
      <h2>Describe Your Service Request</h2>
      <p className="step-intro">
        Please provide details about the service you need. Include information such as:
      </p>
      <ul className="request-hints">
        <li>What type of service do you need? (repair, maintenance, inspection, etc.)</li>
        <li>What issue are you experiencing with your equipment?</li>
        <li>Any specific symptoms or problems you've noticed</li>
        <li>When do you need this service?</li>
        <li>Any other relevant details</li>
      </ul>

      {validationError && (
        <div className="error-message summary-error">{validationError}</div>
      )}

      <div className="form-group">
        <label htmlFor="requestDescription" className="required">
          Service Request Description
        </label>
        <textarea
          id="requestDescription"
          name="requestDescription"
          rows={8}
          value={formData.requestDescription || ''}
          onChange={handleDescriptionChange}
          placeholder="Example: My tractor won't start. The engine cranks but doesn't turn over. I noticed the problem started after heavy rain last week. I need service as soon as possible to prepare for spring planting."
          required
        />
        <div className="character-count">
          {(formData.requestDescription?.length || 0)} characters
        </div>
      </div>
    </div>
  );
}

export default Step5FreeRequest;
